import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, CircularProgress, Grid2, Stack, Typography } from '@mui/material';
import { COLORS, ACTIVITY_FEED_TYPES } from '../../../utils/Application_Constants';
import ActivityFeedItem from './ActivityFeedItem';
import ActivityFeedFilter from './ActivityFeedFilter';

const ActivityFeed = ({ data, error, loading }) => {
    const containerRef = useRef(null);
    const [selectedFilter, setSelectedFilter] = useState('All');

    const filterDictionary = {
        All: () => true,
        Comments: (item) => item.type === ACTIVITY_FEED_TYPES.COMMENT,
        Updates: (item) =>
            item.type === ACTIVITY_FEED_TYPES.STATUS_UPDATE ||
            item.type === ACTIVITY_FEED_TYPES.STATUS_UPDATE_REASON,
    };

    const filteredData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.filter((item) => filterDictionary[selectedFilter](item));
    }, [data, selectedFilter]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = 0;
        }
    }, [selectedFilter, data]);

    return (
        <>
            <Grid2
                container
                justifyContent="space-between"
                alignItems="center"
                mt={0}
                width="100%"
                gap={3}
            >
                <Typography fontWeight="600" fontSize="16px" color={COLORS.LT_JOURNEY175}>
                    Activity Feed
                </Typography>
                <ActivityFeedFilter
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                />
            </Grid2>
            {error && (
                <Typography color={COLORS.LT_PEPPER} fontSize={14} fontWeight="400">
                    {error}
                </Typography>
            )}

            {filteredData && filteredData.length > 0 && !loading && (
                <Box
                    ref={containerRef}
                    mt={0}
                    mb={3}
                    maxHeight={330}
                    overflow="auto"
                    pr={2}
                    sx={{
                        maxHeight: '330px',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '2px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f0f0f0',
                        },
                        scrollbarWidth: 'thin',
                        width: '100%',
                    }}
                >
                    <Stack spacing={3} mt={0}>
                        {filteredData.map((item) => (
                            <ActivityFeedItem key={item.id} item={item} />
                        ))}
                        <Typography textAlign="center" fontSize={12} color={COLORS.LT_MIDNIGHT50}>
                            You’re all caught up!
                        </Typography>
                    </Stack>
                </Box>
            )}
            {filteredData && filteredData.length === 0 && !loading && (
                <Stack alignItems="center" textAlign="center" px={2} gap="12px">
                    <Typography fontSize={14} color={COLORS.LT_MIDNIGHT50} lineHeight="18px">
                        No activity or updates
                    </Typography>
                    <Typography
                        fontSize={14}
                        color={COLORS.LT_MIDNIGHT50}
                        lineHeight="18px"
                        fontWeight="400"
                    >
                        When you comment on a timesheet or change its status, updates will show here
                    </Typography>
                </Stack>
            )}
            {loading && <CircularProgress color="primary" />}
        </>
    );
};

export default ActivityFeed;
